import React from 'react';
import { practitionerQuery_practitioner_availability as Availability } from 'Services/BeGateway/Practitioner/__generated__/practitionerQuery';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'Book/shared';
import { FieldValues, FormValues } from '../../types';
import {
  BackButton,
  BackIcon,
  BackText,
  DateLabel,
  DurationLabel,
  Error,
  Label,
  MessageCaption,
  MessageLengthCounter,
  TextArea,
} from './styles';

const MAX_MESSAGE_LENGTH = 1000;

export interface BookingStageConfirmProps {
  formValues: FormValues;
  setFieldValue(field: string, value: unknown): void;
  practitionerName: string;
  availableTimes: Availability;
  onBackSelected(): void;
  isDirectBookingEnabled: boolean;
  isSubmitting: boolean;
  error?: string;
}

export default ({
  formValues,
  setFieldValue,
  availableTimes,
  practitionerName,
  onBackSelected,
  isDirectBookingEnabled,
  isSubmitting,
  error,
}: BookingStageConfirmProps) => {
  const { t } = useTranslation('book', { keyPrefix: 'practitioner.book' });
  const selectedSlot = formValues[FieldValues.SelectedSlot];
  const message = formValues[FieldValues.Message];
  const messageLength = message?.length ?? 0;
  const isMessageAtLimit = messageLength >= MAX_MESSAGE_LENGTH;
  const labelId = 'text-field-label-id';
  const inputId = 'text-field-id';

  const textareaProps = error ? { 'aria-errormessage': 'message-error' } : {};

  return (
    <>
      {isDirectBookingEnabled && (
        <>
          <DurationLabel
            duration={availableTimes.availabilityConfig?.duration}
          />
          <BackButton disabled={isSubmitting} onClick={onBackSelected}>
            <BackIcon />
            <BackText>{t('ctas.back')}</BackText>
          </BackButton>
        </>
      )}
      {selectedSlot && <DateLabel>{formatDate(selectedSlot.start)}</DateLabel>}
      <Label id={labelId} htmlFor={inputId}>
        {t('label')}
      </Label>
      {error && (
        <Error id="message-error" className="error" role="alert">
          {error}
        </Error>
      )}
      <Field
        name={FieldValues.Message}
        render={() => (
          <TextArea
            {...textareaProps}
            id={inputId}
            name={FieldValues.Message}
            disabled={isSubmitting}
            aria-labelledby={labelId}
            placeholder={t('placeholder')}
            value={formValues[FieldValues.Message]}
            onChange={e => {
              const newMessage = e.target.value;

              if (newMessage.length > MAX_MESSAGE_LENGTH) {
                return;
              }

              setFieldValue(FieldValues.Message, e.target.value);
            }}
          />
        )}
      />
      <MessageLengthCounter isMessageAtLimit={isMessageAtLimit}>
        {message
          ? t('maxCharacters.warning', {
              messageLength,
              maxCharacters: MAX_MESSAGE_LENGTH,
            })
          : t('maxCharacters.placeholder', {
              maxCharacters: MAX_MESSAGE_LENGTH,
            })}
      </MessageLengthCounter>
      <MessageCaption>{t('notes', { practitionerName })}</MessageCaption>
    </>
  );
};
